import React, { useState } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { LifeArea } from '../utils/integratedProfileGenerator';
import { buildAstrologerCoreViewModel, APPROVED_LIFE_AREAS } from '../utils/astrologerCoreViewModel';
import {
  generateCanonicalPredictionFingerprint as generatePredictionFingerprint,
  extractCanonicalAstrologicalEvidence as extractAstrologicalEvidence,
  resolveCanonicalDominanceAndConflict as resolveDominanceAndConflict
} from '../utils/dashaGocharSynthesisEngine';
import { DifferentiationRegressionModal } from './DifferentiationRegressionModal';
import {
  Shield,
  Compass,
  Sparkles,
  Award,
  AlertTriangle,
  Clock,
  BookOpen,
  Sliders,
  CheckCircle2,
  Brain,
  Heart,
  Briefcase,
  TrendingUp,
  Home,
  GraduationCap,
  Globe,
  Flame,
  Activity,
  Layers,
  ChevronRight,
  User,
  ArrowRight,
  Eye,
  FileText,
  Search,
  Zap,
  Info,
  Fingerprint,
  Cpu
} from 'lucide-react';

interface AstrologerCoreViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  onOpenPublishedConsultation?: () => void;
  onSelectLifeAreaTab?: (area: LifeArea) => void;
}

export const AstrologerCoreView: React.FC<AstrologerCoreViewProps> = ({
  kundali,
  lang = 'en',
  onOpenPublishedConsultation,
}) => {
  const [currentLang, setCurrentLang] = useState<Language>(lang);
  const isHi = currentLang === 'hi';

  // Navigation Sub-Views
  const [activeSubView, setActiveSubView] = useState<'core-view' | 'evidence' | 'life-areas' | 'testing'>('core-view');
  
  // QA Differentiation Modal
  const [isQaModalOpen, setIsQaModalOpen] = useState<boolean>(false);

  // Selected Life Area for Deep-Dive workbench
  const [selectedLifeArea, setSelectedLifeArea] = useState<LifeArea>('career');

  // Simulated Age Testing Mode for Testing Workbench
  const [simulatedChildMode, setSimulatedChildMode] = useState<boolean | null>(null);

  // Build View Model
  const effectiveKundali = simulatedChildMode !== null ? {
    ...kundali,
    birthDetails: {
      ...kundali.birthDetails,
      date: simulatedChildMode ? '2020-05-15' : (kundali.birthDetails?.date || '1990-01-01')
    }
  } : kundali;

  const viewModel = buildAstrologerCoreViewModel(effectiveKundali, currentLang);
  const personality = viewModel.personalitySignature;
  const isChild = viewModel.nativeCore.isYoungChild;
  const predictionFingerprint = generatePredictionFingerprint(effectiveKundali);

  const getLifeAreaIcon = (area: LifeArea) => {
    switch (area) {
      case 'career': return <Briefcase className="w-4 h-4" />;
      case 'wealth': return <TrendingUp className="w-4 h-4" />;
      case 'marriage': return <Heart className="w-4 h-4" />;
      case 'progeny': return <User className="w-4 h-4" />;
      case 'health': return <Activity className="w-4 h-4" />;
      case 'property': return <Home className="w-4 h-4" />;
      case 'education': return <GraduationCap className="w-4 h-4" />;
      case 'foreignTravel': return <Globe className="w-4 h-4" />;
      case 'spirituality': return <Sparkles className="w-4 h-4" />;
      default: return <Compass className="w-4 h-4" />;
    }
  };

  const getLifeAreaTitle = (area: LifeArea) => {
    if (isChild) {
      switch (area) {
        case 'career': return isHi ? 'स्वाभाविक प्रतिभा एवं अभिरुचि' : 'Talents & Aptitudes';
        case 'wealth': return isHi ? 'सद्गुण, संस्कार एवं साधनों का सदुपयोग' : 'Value Habits & Stewardship';
        case 'marriage': return isHi ? 'मित्रता, सामाजिक सामंजस्य एवं सहपाठी संबंध' : 'Social & Peer Harmonization';
        case 'progeny': return isHi ? 'पारिवारिक जुड़ाव, स्नेह एवं वात्सल्य' : 'Family Bonds & Nurturing';
        case 'health': return isHi ? 'दैनिक दिनचर्या, शारीरिक ऊर्जा एवं स्वास्थ्य संतुलन' : 'Healthy Routines & Well-being';
        case 'property': return isHi ? 'गृह परिवेश, भावनात्मक सुरक्षा एवं सहज वातावरण' : 'Sense of Home & Security';
        case 'education': return isHi ? 'जिज्ञासा, प्राथमिक शिक्षा एवं बौद्धिक विकास' : 'Learning & Curiosity';
        case 'foreignTravel': return isHi ? 'नवीन अनुभव, यात्राएं एवं अनुकूलन क्षमता' : 'Exploration & Adaptability';
        case 'spirituality': return isHi ? 'नैतिक मूल्य, जिज्ञासा एवं आत्मिक संस्कार' : 'Values, Wonder & Inner Awareness';
      }
    }
    switch (area) {
      case 'career': return isHi ? 'कैरियर व आजीविका' : 'Career & Vocation';
      case 'wealth': return isHi ? 'धन व संपत्ति' : 'Wealth & Assets';
      case 'marriage': return isHi ? 'विवाह व संबंध' : 'Marriage & Partnership';
      case 'progeny': return isHi ? 'संतान व कुल वृद्धि' : 'Progeny & Lineage';
      case 'health': return isHi ? 'स्वास्थ्य व जीवन ऊर्जा' : 'Health & Vitality';
      case 'property': return isHi ? 'भूमि, भवन व वाहन' : 'Property & Conveyance';
      case 'education': return isHi ? 'विद्या व उच्च शिक्षा' : 'Education & Learning';
      case 'foreignTravel': return isHi ? 'विदेश यात्रा व प्रवास' : 'Travel & Relocation';
      case 'spirituality': return isHi ? 'धर्म व आत्म-विकास' : 'Spirituality & Dharma';
    }
  };

  const selectedDeepDive = viewModel.lifeAreaDeepDives[selectedLifeArea];

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 space-y-6 text-astro-text">
      {/* ========================================================================= */}
      {/* MASTER TOP BANNER: Astrologer Core View Workspace                        */}
      {/* ========================================================================= */}
      <div className="bg-gradient-to-r from-astro-primary via-[#113837] to-[#0A2423] text-white p-6 sm:p-7 rounded-3xl shadow-xl border border-astro-accent/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-astro-accent">
            <Shield className="w-4 h-4" />
            <span>{isHi ? 'वेदांग ज्योतिषम् v5.14 — ज्योतिषी संदर्भ मंच' : 'VEDANGJYOTISHAM v5.14 — ASTROLOGER WORKSPACE'}</span>
            {isChild && (
              <span className="bg-astro-accent text-astro-text px-2 py-0.5 rounded-md text-[10px] font-black uppercase">
                {isHi ? 'बालक अवस्था (देश-काल-पात्र)' : 'Child Gating Active'}
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold font-serif flex items-center gap-2.5">
            <span>{isHi ? 'ज्योतिषी कोर व्यू' : 'Astrologer Core View'}</span>
          </h1>
          <p className="text-xs sm:text-sm text-astro-border max-w-2xl leading-relaxed">
            {isHi
              ? 'जन्मजात व्यक्तित्व संरचना, ग्रह बल अक्ष, दशा प्रभाव एवं शास्त्रीय साक्ष्यों का केंद्रीय संश्लेषण मंच।'
              : 'The astrologer’s evidence-and-synthesis workspace: the central reference layer from which all life-area consultations inherit context.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 shrink-0">
          {/* Prediction Differentiation & QA Suite Button */}
          <button
            onClick={() => setIsQaModalOpen(true)}
            className="px-3.5 py-2 rounded-xl bg-astro-primary hover:bg-[#133E3D] text-astro-accent hover:text-astro-accent text-xs font-bold border border-astro-accent/40 transition-all cursor-pointer shadow-xs flex items-center gap-1.5"
          >
            <Cpu className="w-3.5 h-3.5" />
            <span>{isHi ? 'भविष्यवाणी विभेदन सूट' : 'Differentiation Suite (v5.15)'}</span>
          </button>

          {/* Language Switcher */}
          <button
            onClick={() => setCurrentLang(currentLang === 'en' ? 'hi' : 'en')}
            className="px-3.5 py-2 rounded-xl bg-[#1D5E5C] hover:bg-[#236F6D] text-white text-xs font-bold border border-astro-accent/40 transition-all cursor-pointer shadow-xs"
          >
            {currentLang === 'en' ? '🇮🇳 हिन्दी में देखें' : '🌐 View in English'}
          </button>

          {/* Quick link to Published Consultation */}
          {onOpenPublishedConsultation && (
            <button
              onClick={onOpenPublishedConsultation}
              className="px-4 py-2 bg-astro-accent hover:bg-[#A38647] text-astro-text text-xs font-bold rounded-xl shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Eye className="w-3.5 h-3.5" />
              <span>{isHi ? 'प्रकाशित परामर्श देखें' : 'Published Consultation'}</span>
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* MULTI-VIEW NAVIGATION BAR                                                 */}
      {/* ========================================================================= */}
      <div className="flex bg-astro-card p-1.5 rounded-2xl border border-astro-border overflow-x-auto gap-2 shadow-inner">
        {[
          { id: 'core-view', label: isHi ? '१. ज्योतिषी कोर व्यू' : '1. Astrologer Core View', icon: Shield },
          { id: 'evidence', label: isHi ? '२. शास्त्रीय साक्ष्य (Core Data)' : '2. Core Evidence', icon: BookOpen },
          { id: 'life-areas', label: isHi ? '३. जीवन क्षेत्र परामर्श (Deep Dive)' : '3. Life Area Deep Dive', icon: Briefcase },
          { id: 'testing', label: isHi ? '४. परीक्षण एवं सत्यापन (Testing)' : '4. Testing & Verification', icon: Sliders },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubView(tab.id as any)}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all cursor-pointer ${
                activeSubView === tab.id
                  ? 'bg-astro-primary text-white shadow-md'
                  : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-border/50'
              }`}
            >
              <Icon className="w-4 h-4 text-astro-accent" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ========================================================================= */}
      {/* SUB-VIEW 1: ASTROLOGER CORE VIEW (Master Synthesis Dashboard)             */}
      {/* ========================================================================= */}
      {activeSubView === 'core-view' && (
        <div className="space-y-6 animate-fade-in">
          
          {/* A. NATIVE CORE FACTS BAR */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-xs">
            <div className="text-xs font-bold text-astro-accent uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" />
              <span>{isHi ? 'मूल कुंडली विवरण (Native Core Facts)' : 'A. Native Core Facts'}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 text-center">
              <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                <div className="text-[10px] text-astro-secondary-text font-medium">{isHi ? 'लग्न' : 'Lagna'}</div>
                <div className="text-sm font-bold text-astro-primary">{viewModel.nativeCore.lagnaSign}</div>
              </div>
              <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                <div className="text-[10px] text-astro-secondary-text font-medium">{isHi ? 'लग्नेश' : 'Lagna Lord'}</div>
                <div className="text-sm font-bold text-astro-primary">{viewModel.nativeCore.lagnaLord}</div>
              </div>
              <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                <div className="text-[10px] text-astro-secondary-text font-medium">{isHi ? 'चंद्र राशि' : 'Moon Sign'}</div>
                <div className="text-sm font-bold text-astro-primary">{viewModel.nativeCore.moonSign}</div>
              </div>
              <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                <div className="text-[10px] text-astro-secondary-text font-medium">{isHi ? 'नक्षत्र' : 'Nakshatra'}</div>
                <div className="text-sm font-bold text-astro-primary truncate">{viewModel.nativeCore.nakshatra}</div>
              </div>
              <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                <div className="text-[10px] text-astro-secondary-text font-medium">{isHi ? 'पाद' : 'Pada'}</div>
                <div className="text-sm font-bold text-astro-primary">पाद {viewModel.nativeCore.pada}</div>
              </div>
              <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                <div className="text-[10px] text-astro-secondary-text font-medium">{isHi ? 'आयु' : 'Age'}</div>
                <div className="text-sm font-bold text-astro-primary">
                  {viewModel.nativeCore.nativeAge !== null ? `${viewModel.nativeCore.nativeAge} yrs` : 'N/A'}
                  {isChild && <span className="text-[9px] block text-astro-accent font-semibold">{isHi ? '(बालक)' : '(Child)'}</span>}
                </div>
              </div>
              <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                <div className="text-[10px] text-astro-secondary-text font-medium">{isHi ? 'वर्तमान महादशा' : 'Current MD'}</div>
                <div className="text-sm font-bold text-astro-accent">{viewModel.nativeCore.currentMahadasha}</div>
              </div>
              <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                <div className="text-[10px] text-astro-secondary-text font-medium">{isHi ? 'वर्तमान अंतर्दशा' : 'Current AD'}</div>
                <div className="text-sm font-bold text-astro-accent">{viewModel.nativeCore.currentAntardasha}</div>
              </div>
            </div>
          </div>

          {/* B & C. PERSONALITY SIGNATURE & PLANETARY STRENGTH AXIS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* B. PERSONALITY SIGNATURE */}
            <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-astro-accent" />
                  <h3 className="text-base font-bold font-serif text-astro-primary">
                    {isHi ? 'ख. व्यक्तित्व हस्ताक्षर (Personality Signature)' : 'B. Personality Signature'}
                  </h3>
                </div>
                <span className="text-[11px] font-semibold bg-[#FAF6ED] text-astro-secondary-text px-2.5 py-1 rounded-lg border border-astro-border">
                  v5.13 Root Signature
                </span>
              </div>

              <div className="space-y-3.5 text-xs sm:text-sm">
                <div className="p-3 rounded-xl bg-[#FAF6ED]/70 border border-astro-border/70">
                  <div className="font-bold text-astro-primary mb-1 flex items-center gap-1.5">
                    <Compass className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? 'लग्न स्वरूप (Outer Vessel):' : 'Lagna Archetype (Outer Vessel):'}</span>
                    <span className="text-astro-accent font-serif">{personality.lagnaArchetype.sign}</span>
                  </div>
                  <p className="text-[#384342] leading-relaxed">
                    {isHi ? personality.lagnaArchetype.presenceHi : personality.lagnaArchetype.presenceEn}
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-[#FAF6ED]/70 border border-astro-border/70">
                  <div className="font-bold text-astro-primary mb-1 flex items-center gap-1.5">
                    <Brain className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? 'मानसिक स्वभाव (Mind Archetype):' : 'Mind & Cognitive Style:'}</span>
                    <span className="text-astro-accent font-serif">{personality.mindArchetype.nakshatra}</span>
                  </div>
                  <p className="text-[#384342] leading-relaxed">
                    {isHi ? personality.mindArchetype.cognitiveStyleHi : personality.mindArchetype.cognitiveStyleEn}
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-[#FAF6ED]/70 border border-astro-border/70">
                  <div className="font-bold text-astro-primary mb-1 flex items-center gap-1.5">
                    <Heart className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? 'भावनात्मक केंद्र (Emotional Core):' : 'Emotional Core:'}</span>
                    <span className="text-astro-accent font-serif">{personality.emotionalCore.moonSign}</span>
                  </div>
                  <p className="text-[#384342] leading-relaxed">
                    {isHi ? personality.emotionalCore.receptivityHi : personality.emotionalCore.receptivityEn}
                  </p>
                </div>
              </div>
            </div>

            {/* C. PLANETARY STRENGTH AXIS */}
            <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-astro-accent" />
                  <h3 className="text-base font-bold font-serif text-astro-primary">
                    {isHi ? 'ग. ग्रह बल अक्ष (Planetary Strength Axis)' : 'C. Planetary Strength Axis'}
                  </h3>
                </div>
                <span className="text-[11px] font-semibold bg-[#FAF6ED] text-astro-secondary-text px-2.5 py-1 rounded-lg border border-astro-border">
                  Shadbala Contrast
                </span>
              </div>

              {/* Strongest vs Weakest Cards */}
              <div className="space-y-4 text-xs sm:text-sm">
                {/* Strongest Planet */}
                <div className="p-4 rounded-2xl bg-emerald-50/60 border border-emerald-200/80 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-bold text-emerald-900 text-sm">
                      <Zap className="w-4 h-4 text-emerald-700" />
                      <span>{isHi ? 'सर्वोच्च बलि ग्रह (Strongest Engine):' : 'Strongest Executive Engine:'}</span>
                      <span className="font-serif text-base">{viewModel.strengthAxis.strongest.planet}</span>
                    </div>
                    <span className="bg-emerald-700 text-white font-bold px-2.5 py-0.5 rounded-full text-xs">
                      {viewModel.strengthAxis.strongest.rupas} Rupas
                    </span>
                  </div>
                  <p className="text-[#384342] leading-relaxed">
                    {isHi ? viewModel.strengthAxis.strongest.strengthDescriptionHi : viewModel.strengthAxis.strongest.strengthDescriptionEn}
                  </p>
                  <div className="text-[11px] font-semibold text-emerald-800 bg-emerald-100/50 p-2 rounded-lg">
                    <span className="font-bold">{isHi ? 'अति-उपयोग प्रवृत्ति: ' : 'Potential Overuse Pattern: '}</span>
                    {isHi ? viewModel.strengthAxis.strongest.overusePatternHi : viewModel.strengthAxis.strongest.overusePatternEn}
                  </div>
                </div>

                {/* Weakest Planet */}
                <div className="p-4 rounded-2xl bg-astro-secondary-surface/60 border border-[#E2DED5]/80 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-bold text-astro-secondary text-sm">
                      <AlertTriangle className="w-4 h-4 text-[#52635F]" />
                      <span>{isHi ? 'संवेदनशील ग्रह (Weakest Vulnerability):' : 'Weakest Vulnerability Planet:'}</span>
                      <span className="font-serif text-base">{viewModel.strengthAxis.weakest.planet}</span>
                    </div>
                    <span className="bg-amber-700 text-white font-bold px-2.5 py-0.5 rounded-full text-xs">
                      {viewModel.strengthAxis.weakest.rupas} Rupas
                    </span>
                  </div>
                  <p className="text-[#384342] leading-relaxed">
                    {isHi ? viewModel.strengthAxis.weakest.vulnerabilityHi : viewModel.strengthAxis.weakest.vulnerabilityEn}
                  </p>
                  <div className="text-[11px] font-semibold text-astro-secondary bg-astro-secondary/15/50 p-2 rounded-lg">
                    <span className="font-bold">{isHi ? 'सुधारात्मक ध्यान: ' : 'Corrective Focus: '}</span>
                    {isHi ? viewModel.strengthAxis.weakest.correctiveFocusHi : viewModel.strengthAxis.weakest.correctiveFocusEn}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* D. LAGNA-MOON POLARITY & G. AGE MATURATION */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* D. LAGNA-MOON POLARITY */}
            <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 shadow-sm space-y-3.5">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <Sliders className="w-5 h-5 text-astro-accent" />
                  <h3 className="text-base font-bold font-serif text-astro-primary">
                    {isHi ? 'घ. लग्न-चंद्र ध्रुवीय संतुलन (Lagna–Moon Polarity)' : 'D. Lagna–Moon Polarity'}
                  </h3>
                </div>
                <span className="text-[11px] font-semibold bg-[#FAF6ED] text-astro-secondary-text px-2.5 py-1 rounded-lg border border-astro-border">
                  Section 8 Synthesis
                </span>
              </div>

              <div className="space-y-2.5 text-xs sm:text-sm">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                    <span className="font-bold text-astro-primary block">{isHi ? 'बाह्य स्वरूप (Outer):' : 'Outer Expression:'}</span>
                    <span className="text-astro-secondary-text">{viewModel.nativeCore.lagnaSign} ({personality.lagnaArchetype.element})</span>
                  </div>
                  <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border">
                    <span className="font-bold text-astro-primary block">{isHi ? 'आंतरिक मन (Inner):' : 'Inner Mind:'}</span>
                    <span className="text-astro-secondary-text">{viewModel.nativeCore.moonSign} ({personality.emotionalCore.element})</span>
                  </div>
                </div>
                <div className="p-3.5 rounded-xl bg-[#FAF6ED]/90 border border-astro-border text-[#384342] leading-relaxed">
                  <span className="font-bold text-astro-primary block mb-1">{isHi ? 'ध्रुवीय संश्लेषण निष्कर्ष (Core Polarity):' : 'Synthesized Polarity Dynamic:'}</span>
                  {isHi ? viewModel.polarity.corePolarityHi : viewModel.polarity.corePolarityEn}
                </div>
              </div>
            </div>

            {/* G. AGE MATURATION */}
            <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 shadow-sm space-y-3.5">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-astro-accent" />
                  <h3 className="text-base font-bold font-serif text-astro-primary">
                    {isHi ? 'छ. आयु परिपक्वता चक्र (Age Maturation Arc)' : 'G. Age Maturation Arc'}
                  </h3>
                </div>
                <span className="text-[11px] font-semibold bg-[#FAF6ED] text-astro-secondary-text px-2.5 py-1 rounded-lg border border-astro-border">
                  Section 9 Arc
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-[#FAF6ED]/90 border border-astro-border text-xs sm:text-sm text-[#384342] leading-relaxed space-y-2">
                <div className="font-bold text-astro-primary flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'आयु के साथ व्यक्तित्व का शास्त्रीय विकास:' : 'How this native\'s pattern evolves with age:'}</span>
                </div>
                <p>
                  {isHi ? viewModel.ageMaturation.maturationArcHi : viewModel.ageMaturation.maturationArcEn}
                </p>
              </div>
            </div>
          </div>

          {/* F. TIMING VS INHERENT CAPACITY PANEL */}
          <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-astro-accent" />
                <h3 className="text-base font-bold font-serif text-astro-primary">
                  {isHi ? 'च. काल-प्रवाह एवं जन्मजात क्षमता विभाजन (Timing Panel)' : 'F. Inherent Capacity vs. Active Timing'}
                </h3>
              </div>
              <span className="text-[11px] font-semibold bg-[#FAF6ED] text-astro-secondary-text px-2.5 py-1 rounded-lg border border-astro-border">
                Strict Separation
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm">
              <div className="p-4 rounded-2xl bg-[#FAF6ED]/70 border border-astro-border space-y-2">
                <div className="font-bold text-astro-primary flex items-center gap-1.5">
                  <Layers className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'जन्मजात क्षमता आधार (Inherent Capacity):' : 'Inherent Chart Capacity:'}</span>
                </div>
                <p className="text-[#384342] leading-relaxed">
                  {isHi ? viewModel.timing.inherentCapacitySummaryHi : viewModel.timing.inherentCapacitySummaryEn}
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-[#FAF6ED]/70 border border-astro-border space-y-2">
                <div className="font-bold text-astro-accent flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'सक्रिय काल-प्रवाह (Active Timing Window):' : 'Active Timing Window (Vimshottari):'}</span>
                </div>
                <p className="text-[#384342] leading-relaxed">
                  {isHi ? viewModel.timing.activeTimingSummaryHi : viewModel.timing.activeTimingSummaryEn}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-VIEW 2: CORE EVIDENCE (Astrological Core Data Matrix)                 */}
      {/* ========================================================================= */}
      {activeSubView === 'evidence' && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-4">
              <div>
                <h3 className="text-xl font-bold font-serif text-astro-primary flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-astro-accent" />
                  <span>{isHi ? 'शास्त्रीय मूल साक्ष्य तालिका (Core Evidence Matrix)' : 'Astrological Core Data & Raw Evidence'}</span>
                </h3>
                <p className="text-xs text-astro-secondary-text mt-1">
                  {isHi 
                    ? 'भाव संरचना, अष्टकवर्ग रेखांक, षड्बल रूप तथा वर्ग कुंडलियों का प्रत्यक्ष साक्ष्य।' 
                    : 'Inspect the underlying evidence: Bhava structure, SAV bindus, Shadbala Rupas, and divisional charts.'}
                </p>
              </div>
            </div>

            {/* 1. 12 BHAVAS & SAV BINDUS TABLE */}
            <div className="space-y-3">
              <h4 className="text-sm font-bold text-astro-primary flex items-center gap-2">
                <Layers className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? '१. द्वादश भाव संरचना एवं सर्वाष्टकवर्ग रेखांक (Bhava & SAV Matrix)' : '1. 12 Bhavas Structure & SAV Bindus'}</span>
              </h4>
              <div className="overflow-x-auto rounded-2xl border border-astro-border">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#FAF6ED] text-astro-primary border-b border-astro-border font-bold">
                      <th className="p-3">{isHi ? 'भाव' : 'House'}</th>
                      <th className="p-3">{isHi ? 'राशि' : 'Sign'}</th>
                      <th className="p-3">{isHi ? 'भावेश' : 'Lord'}</th>
                      <th className="p-3">{isHi ? 'स्थित ग्रह' : 'Occupants'}</th>
                      <th className="p-3 text-center">{isHi ? 'SAV रेखांक' : 'SAV Bindus'}</th>
                      <th className="p-3">{isHi ? 'वर्गीकरण' : 'Classification'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-astro-border/70 text-[#384342]">
                    {viewModel.evidence.bhavas.map((b) => (
                      <tr key={b.houseNumber} className="hover:bg-[#FAF6ED]/50 transition-colors">
                        <td className="p-3 font-bold text-astro-primary">भाव {b.houseNumber}</td>
                        <td className="p-3 font-medium">{b.sign}</td>
                        <td className="p-3 font-medium">{b.signLord}</td>
                        <td className="p-3">
                          {b.occupants.length > 0 ? (
                            <span className="font-semibold text-astro-accent">{b.occupants.join(', ')}</span>
                          ) : (
                            <span className="text-astro-secondary-text italic">{isHi ? 'रिक्त' : 'Empty'}</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`font-bold px-2 py-0.5 rounded-md text-xs ${
                            b.savBindus >= 30 ? 'bg-emerald-100 text-emerald-800' :
                            b.savBindus <= 25 ? 'bg-astro-secondary/15 text-astro-secondary' : 'bg-astro-secondary-surface text-astro-text'
                          }`}>
                            {b.savBindus}
                          </span>
                        </td>
                        <td className="p-3 text-[11px]">
                          {b.isKendra && <span className="bg-astro-primary/10 text-astro-primary font-bold px-1.5 py-0.5 rounded mr-1">केंद्र</span>}
                          {b.isTrikona && <span className="bg-astro-accent/10 text-astro-accent font-bold px-1.5 py-0.5 rounded mr-1">त्रिकोण</span>}
                          {b.isDusthana && <span className="bg-red-100 text-red-800 font-bold px-1.5 py-0.5 rounded">दुस्थान</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 2. SHADBALA RANKING TABLE */}
            <div className="space-y-3">
              <h4 className="text-sm font-bold text-astro-primary flex items-center gap-2">
                <Award className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? '२. ग्रह षड्बल रूप तालिका (Planetary Strength Breakdown)' : '2. Planetary Shadbala Rupas'}</span>
              </h4>
              <div className="overflow-x-auto rounded-2xl border border-astro-border">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#FAF6ED] text-astro-primary border-b border-astro-border font-bold">
                      <th className="p-3">{isHi ? 'ग्रह' : 'Planet'}</th>
                      <th className="p-3">{isHi ? 'षड्बल रूप' : 'Total Rupas'}</th>
                      <th className="p-3">{isHi ? 'श्रेणी' : 'Category'}</th>
                      <th className="p-3">{isHi ? 'विशिष्ट स्थिति' : 'Distinction'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-astro-border/70 text-[#384342]">
                    {viewModel.evidence.planetaryStrengths.map((p) => (
                      <tr key={p.planet} className="hover:bg-[#FAF6ED]/50 transition-colors">
                        <td className="p-3 font-bold text-astro-primary">{p.planet}</td>
                        <td className="p-3 font-bold">{p.rupas}</td>
                        <td className="p-3">{p.category}</td>
                        <td className="p-3">
                          {p.isStrongest && (
                            <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-md text-[10px]">
                              {isHi ? '★ सर्वोच्च बलि ग्रह' : '★ Strongest Planet'}
                            </span>
                          )}
                          {p.isWeakest && (
                            <span className="bg-astro-secondary/15 text-astro-secondary font-bold px-2 py-0.5 rounded-md text-[10px]">
                              ⚠ सबसे संवेदनशील ग्रह
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 3. DIVISIONAL CHARTS AUDIT (Live calculated vs Metadata) */}
            <div className="space-y-3">
              <h4 className="text-sm font-bold text-astro-primary flex items-center gap-2">
                <Compass className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? '३. वर्ग कुंडली साक्ष्य तालिका (Divisional Charts Live Evidence)' : '3. Divisional Charts Live Calculated Evidence'}</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                {viewModel.evidence.divisionalCharts.map((dc) => (
                  <div key={dc.code} className="p-4 rounded-2xl bg-[#FAF6ED]/70 border border-astro-border space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-astro-primary text-sm">{dc.title}</span>
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                        dc.isLiveCalculated ? 'bg-emerald-100 text-emerald-800' : 'bg-astro-card text-astro-secondary-text'
                      }`}>
                        {isHi ? dc.statusTextHi : dc.statusTextEn}
                      </span>
                    </div>
                    <p className="text-astro-secondary-text text-[11px]">{dc.purpose}</p>
                    <div className="text-[11px] font-medium text-astro-accent pt-1 border-t border-astro-border/60">
                      {isHi ? dc.keyPlacementHi : dc.keyPlacementEn}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-VIEW 3: LIFE AREA DEEP DIVE WORKBENCH                                 */}
      {/* ========================================================================= */}
      {activeSubView === 'life-areas' && (
        <div className="space-y-6 animate-fade-in">
          
          {/* Life Area Selector Tabs */}
          <div className="bg-astro-bg border border-astro-border rounded-3xl p-5 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border pb-3">
              <div>
                <h3 className="text-base font-bold font-serif text-astro-primary flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-astro-accent" />
                  <span>{isHi ? 'जीवन क्षेत्र चयन (Select Life Area)' : 'Life Area Deep Dive Selector'}</span>
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi 
                    ? 'समान व्यक्तित्व हस्ताक्षर + विभिन्न जीवन विषय = समन्वित शास्त्रीय परामर्श।' 
                    : 'Same native + same personality signature + different life area context.'}
                </p>
              </div>
              {isChild && (
                <span className="text-[11px] bg-[#FAF6ED] text-astro-accent font-bold px-3 py-1 rounded-xl border border-astro-border">
                  {isHi ? 'बालक विकासात्मक वर्गीकरण लागू' : 'Child Developmental Gating Active'}
                </span>
              )}
            </div>

            {/* 9 Life Area Buttons */}
            <div className="flex flex-wrap gap-2">
              {APPROVED_LIFE_AREAS.map((area) => {
                const isSelected = selectedLifeArea === area;
                return (
                  <button
                    key={area}
                    onClick={() => setSelectedLifeArea(area)}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
                      isSelected
                        ? 'bg-astro-primary text-white border-astro-primary shadow-md scale-[1.02]'
                        : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border hover:bg-astro-border/70'
                    }`}
                  >
                    {getLifeAreaIcon(area)}
                    <span>{getLifeAreaTitle(area)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Deep-Dive Consultation Panel */}
          {selectedDeepDive && (
            <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
              
              {/* Header */}
              <div className="border-b border-astro-border pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <span className="text-xs font-bold text-astro-accent uppercase tracking-wider block mb-1">
                    {isHi ? 'विशिष्ट जीवन क्षेत्र संश्लेषण' : 'Inherited Core Synthesis'}
                  </span>
                  <h3 className="text-xl sm:text-2xl font-bold font-serif text-astro-primary flex items-center gap-2.5">
                    {getLifeAreaIcon(selectedLifeArea)}
                    <span>{selectedDeepDive.title}</span>
                  </h3>
                </div>
                <div className="text-xs bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border text-astro-secondary-text">
                  <span className="font-bold text-astro-primary block">{isHi ? 'विरासत में मिला व्यक्तित्व आधार:' : 'Inherited Core Context:'}</span>
                  <span>{personality.lagnaArchetype.sign} Lagna • {personality.emotionalCore.moonSign} Moon • {personality.dominantExecutiveEngine.planet} Engine</span>
                </div>
              </div>

              {/* 5 Consultation Dimensions */}
              <div className="space-y-4 text-xs sm:text-sm text-[#384342]">
                
                {/* 1. Astrological Lens */}
                <div className="p-4 rounded-2xl bg-[#FAF6ED]/70 border border-astro-border space-y-1.5">
                  <div className="font-bold text-astro-primary text-xs flex items-center gap-1.5 uppercase tracking-wide">
                    <Compass className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? '१. शास्त्रीय दृष्टिकोण (Astrological Lens):' : '1. Astrological Lens & House Network:'}</span>
                  </div>
                  <p className="leading-relaxed">{selectedDeepDive.astrologicalLens}</p>
                </div>

                {/* 2. Baseline Capacity (Primary + Secondary Bhava Synthesis) */}
                <div className="p-4 rounded-2xl bg-[#FAF6ED]/70 border border-astro-border space-y-1.5">
                  <div className="font-bold text-astro-primary text-xs flex items-center gap-1.5 uppercase tracking-wide">
                    <Layers className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? '२. आधारभूत जन्मजात क्षमता (Baseline Capacity):' : '2. Baseline Capacity & Secondary House Synthesis:'}</span>
                  </div>
                  <p className="leading-relaxed whitespace-pre-line">{selectedDeepDive.baselineCapacity}</p>
                </div>

                {/* 3. Active Timing Influence */}
                <div className="p-4 rounded-2xl bg-[#FAF6ED]/70 border border-astro-border space-y-1.5">
                  <div className="font-bold text-astro-accent text-xs flex items-center gap-1.5 uppercase tracking-wide">
                    <Clock className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? '३. सक्रिय काल-प्रवाह प्रभाव (Active Timing Influence):' : '3. Active Timing Influence (Vimshottari Dasha):'}</span>
                  </div>
                  <p className="leading-relaxed">{selectedDeepDive.activeTimingInfluence}</p>
                </div>

                {/* 4. Risk Mitigation (Weakest Planet Vulnerability) */}
                <div className="p-4 rounded-2xl bg-astro-secondary-surface/60 border border-[#E2DED5]/80 space-y-1.5">
                  <div className="font-bold text-astro-secondary text-xs flex items-center gap-1.5 uppercase tracking-wide">
                    <AlertTriangle className="w-3.5 h-3.5 text-[#52635F]" />
                    <span>{isHi ? '४. जोखिम निवारण व सावधानी (Risk Mitigation):' : '4. Risk Mitigation & Vulnerability Safeguard:'}</span>
                  </div>
                  <p className="leading-relaxed text-[#384342]">{selectedDeepDive.riskMitigation}</p>
                </div>

                {/* 5. Action Roadmap (3 Directives + Remedial Focus) */}
                <div className="p-4 rounded-2xl bg-emerald-50/60 border border-emerald-200/80 space-y-2">
                  <div className="font-bold text-emerald-900 text-xs flex items-center gap-1.5 uppercase tracking-wide">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                    <span>{isHi ? '५. व्यावहारिक कार्ययोजना (Action Roadmap & Practical Focus):' : '5. Action Roadmap & Practical Directives:'}</span>
                  </div>
                  <ul className="list-disc pl-5 space-y-1 text-xs">
                    {selectedDeepDive.actionRoadmap.directives.map((dir, i) => (
                      <li key={i} className="leading-relaxed text-[#384342]">{dir}</li>
                    ))}
                  </ul>
                  <div className="text-[11px] font-semibold text-emerald-900 bg-emerald-100/60 p-2.5 rounded-xl mt-2">
                    <span className="font-bold">{isHi ? 'शास्त्रीय उपाय निर्णय: ' : 'Remedial Focus: '}</span>
                    {selectedDeepDive.actionRoadmap.remedialFocus}
                  </div>
                </div>
              </div>

              {/* Underlying Factor Extraction Inspector for Astrologer */}
              {selectedDeepDive.extractedFactors && (
                <div className="pt-4 border-t border-astro-border space-y-3">
                  <div className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1.5">
                    <Sliders className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{isHi ? 'ज्योतिषी कारक निष्कर्षण साक्ष्य (Extracted Factors Inspector)' : 'Extracted Factors Inspector'}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div className="bg-[#FAF6ED] p-3 rounded-xl border border-astro-border">
                      <span className="font-bold text-astro-primary block mb-1">{isHi ? 'प्राथमिक भाव:' : 'Primary Houses:'}</span>
                      {selectedDeepDive.extractedFactors.primaryHousesData.map(h => (
                        <div key={h.houseNumber} className="text-[11px] text-[#384342]">
                          भाव {h.houseNumber} ({h.sign}, Lord: {h.signLord}, SAV: {h.savBindus})
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#FAF6ED] p-3 rounded-xl border border-astro-border">
                      <span className="font-bold text-astro-primary block mb-1">{isHi ? 'द्वितीयक भाव:' : 'Secondary Houses:'}</span>
                      {selectedDeepDive.extractedFactors.secondaryHousesData.map(h => (
                        <div key={h.houseNumber} className="text-[11px] text-[#384342]">
                          भाव {h.houseNumber} ({h.sign}, Lord: {h.signLord}, SAV: {h.savBindus})
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#FAF6ED] p-3 rounded-xl border border-astro-border">
                      <span className="font-bold text-astro-primary block mb-1">{isHi ? 'दशा सक्रियता:' : 'Active Dasha Relation:'}</span>
                      <div className="text-[11px] text-[#384342]">
                        MD: {selectedDeepDive.extractedFactors.activeDashaRelation.mahadashaLord}
                        {selectedDeepDive.extractedFactors.activeDashaRelation.activatesHouse && (
                          <span className="text-emerald-700 block font-semibold">
                            ✓ Activates House {selectedDeepDive.extractedFactors.activeDashaRelation.activatedHouseNumbers.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-VIEW 4: TESTING & VERIFICATION WORKBENCH                             */}
      {/* ========================================================================= */}
      {activeSubView === 'testing' && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div>
                <h3 className="text-lg font-bold font-serif text-astro-primary flex items-center gap-2">
                  <Sliders className="w-5 h-5 text-astro-accent" />
                  <span>{isHi ? 'परीक्षण एवं सत्यापन कार्यक्षेत्र (Testing & Verification Workbench)' : 'Testing & Verification Workbench'}</span>
                </h3>
                <p className="text-xs text-astro-secondary-text mt-0.5">
                  {isHi 
                    ? 'आयु अनुकरण, भाषा परिवर्तन, डेटा अखंडता तथा पर्सनैलिटी सिग्नेचर का सत्यापन करें।' 
                    : 'Test adult vs child chart gating, verify deterministic factor flow, and inspect PersonalitySignature.'}
                </p>
              </div>
            </div>

            {/* Test Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
              <div className="bg-[#FAF6ED] p-4 rounded-2xl border border-astro-border space-y-2">
                <span className="font-bold text-astro-primary block">{isHi ? 'आयु अनुकरण (Child Gating Test):' : 'Simulate Age Gate:'}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSimulatedChildMode(true)}
                    className={`flex-1 py-2 rounded-xl font-bold transition-all border ${
                      simulatedChildMode === true ? 'bg-astro-primary text-white border-astro-primary' : 'bg-astro-bg text-astro-secondary-text border-astro-border'
                    }`}
                  >
                    {isHi ? 'बालक (आयु < ११)' : 'Child (Age < 11)'}
                  </button>
                  <button
                    onClick={() => setSimulatedChildMode(false)}
                    className={`flex-1 py-2 rounded-xl font-bold transition-all border ${
                      simulatedChildMode === false ? 'bg-astro-primary text-white border-astro-primary' : 'bg-astro-bg text-astro-secondary-text border-astro-border'
                    }`}
                  >
                    {isHi ? 'वयस्क (Adult)' : 'Adult (Age > 18)'}
                  </button>
                  <button
                    onClick={() => setSimulatedChildMode(null)}
                    className={`px-3 py-2 rounded-xl font-bold transition-all border ${
                      simulatedChildMode === null ? 'bg-astro-primary text-white border-astro-primary' : 'bg-astro-bg text-astro-secondary-text border-astro-border'
                    }`}
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="bg-[#FAF6ED] p-4 rounded-2xl border border-astro-border space-y-2">
                <span className="font-bold text-astro-primary block">{isHi ? 'सत्यापन स्थिति (Verification Matrix):' : 'Integrity Checklist:'}</span>
                <div className="space-y-1 text-[11px] text-[#384342]">
                  <div className="flex items-center gap-1.5 text-emerald-800">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                    <span>v5.13 Synthesis Logic Frozen & Intact</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-800">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                    <span>Deterministic Factor Extraction Active</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-800">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                    <span>Child Desh-Kaal-Patra Gating Verified</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#FAF6ED] p-4 rounded-2xl border border-astro-border space-y-2">
                <span className="font-bold text-astro-primary block">{isHi ? 'भाषा जांच (Language Parity):' : 'Language Parity:'}</span>
                <p className="text-[11px] text-astro-secondary-text">
                  {isHi ? 'सम्पूर्ण परामर्श और तकनीकी साक्ष्य हिन्दी एवं अंग्रेजी में शत-प्रतिशत समतुल्य हैं।' : 'Full semantic parity maintained across English and Hindi interfaces.'}
                </p>
              </div>
            </div>

            {/* Raw PersonalitySignature JSON Inspector */}
            <div className="space-y-2">
              <span className="font-bold text-xs text-astro-primary block">
                {isHi ? 'कच्चा पर्सनैलिटी सिग्नेचर डेटा (Raw PersonalitySignature JSON):' : 'Raw PersonalitySignature Object:'}
              </span>
              <pre className="bg-[#1D2524] text-emerald-400 p-4 rounded-2xl text-[11px] font-mono overflow-x-auto max-h-72 border border-[#384342]">
                {JSON.stringify(personality, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Differentiation & Verification Suite Modal */}
      <DifferentiationRegressionModal
        isOpen={isQaModalOpen}
        onClose={() => setIsQaModalOpen(false)}
        lang={currentLang}
      />
    </div>
  );
};

export default AstrologerCoreView;
